// FILE: prPreviews.logic.ts
// Purpose: Pure helpers behind the list of isolated pull-request previews — how
//          a status reads, how long one has left, and which of them a surface
//          should show — kept apart from the components so the wording is
//          testable without a DOM and identical in the environment card and in
//          the pull request panel.
// Exports: `previewStatusPill`, `describePreviewOperation`,
//          `describePreviewExpiry`, `livePreviewsOf`, `alivePreviewsOf`,
//          `previewOfPullRequest`.

import type { PrPreviewRow } from "../../auth/EnvironmentsClient";
import { remainingMsUntil } from "../../lib/relativeTime";
import type { SyncPill } from "./projectEnvironments.logic";

/**
 * The pill that says what state a preview is in.
 *
 * Shares `SyncPill`'s shape with the environment pills so the two lists look
 * like one screen, and `conflict` is amber rather than red on purpose: nothing
 * is broken and nobody has to be told off — the branch does not merge onto the
 * environment's commit, and it becomes buildable the moment the author rebases.
 * `failed` is the red one, because that is the state that means the server tried
 * and something went wrong.
 */
export function previewStatusPill(status: PrPreviewRow["status"]): SyncPill {
  switch (status) {
    case "provisioning":
      return {
        label: "Construyendo",
        className: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
        dotClassName: "bg-sky-500",
        hint: "Se está creando el worktree, copiando los datos y ejecutando el setup. Tarda lo que tarde el hook del proyecto.",
      };
    case "ready":
      return {
        label: "Lista",
        className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
        dotClassName: "bg-emerald-500",
        hint: "La preview responde en su URL, con su propia copia de los datos del entorno.",
      };
    case "conflict":
      return {
        label: "Conflicto",
        className: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
        dotClassName: "bg-amber-500",
        hint: "El PR no se puede mergear sobre el commit del entorno. No hay worktree ni base de datos: cuando el autor rebase, reconstruirla la deja lista con la misma URL.",
      };
    case "failed":
      return {
        label: "Falló",
        className: "bg-red-500/15 text-red-600 dark:text-red-300",
        dotClassName: "bg-red-500",
        hint: "La construcción se cayó a mitad. Puede quedar algo que limpiar: destruirla libera lo que haya.",
      };
    case "destroyed":
      return {
        label: "Destruida",
        className: "bg-muted/50 text-muted-foreground",
        dotClassName: "bg-muted-foreground/40",
        hint: "Ya no existe. La fila se conserva como historia y el hueco del PR está libre.",
      };
  }
}

/**
 * What each preview operation is called while it is happening.
 *
 * Separate from the environment's `describeActiveOperation` because the subject
 * is different: an environment's line is about the environment («Actualizando…
 * por Ana»), and a preview's has to name the pull request, which is the only
 * thing that tells two simultaneous builds apart.
 */
const PREVIEW_OPERATION_VERB: Readonly<Record<string, (prNumber: number) => string>> = {
  "preview-create": (prNumber) => `Construyendo preview del PR #${prNumber}…`,
  "preview-refresh": (prNumber) => `Reconstruyendo la preview del PR #${prNumber}…`,
  "preview-destroy": (prNumber) => `Destruyendo la preview del PR #${prNumber}…`,
};

/**
 * «Construyendo preview del PR #131… por Ana».
 *
 * An operation name this build has not heard of is not an error — the mutex is
 * shared with the environment's own operations, and any holder at all means the
 * preview is busy, which is the part the button needs.
 */
export function describePreviewOperation(input: {
  readonly operation: string;
  readonly prNumber: number;
  /** Null when auth is off, or the directory has not loaded yet. */
  readonly actorName: string | null;
}): string {
  const verb =
    PREVIEW_OPERATION_VERB[input.operation]?.(input.prNumber) ??
    `Trabajando en la preview del PR #${input.prNumber}…`;
  return input.actorName === null ? verb : `${verb} por ${input.actorName}`;
}

/**
 * «caduca en 2 d», «caducada», or null when there is no expiry to speak of.
 *
 * Coarse, in the largest unit that is still true, for the reason the public
 * countdown is: a preview expiring is a thing that happens by itself, and a
 * countdown to the minute invites reading it as a deadline somebody has to meet.
 *
 * Null and «caducada» are deliberately different answers. A `conflict` holds
 * nothing, so it has no `expiresAt` and saying anything about its lifetime
 * would be inventing one; a `ready` preview whose expiry has passed is waiting
 * for the sweep, and hiding that would leave a URL on screen that is about to
 * stop answering.
 */
export function describePreviewExpiry(input: {
  readonly expiresAt: string | null;
  readonly nowMs: number;
}): string | null {
  if (input.expiresAt === null) return null;
  const remaining = remainingMsUntil(input.expiresAt, input.nowMs);
  if (remaining === null) return "caducada";
  const minutes = Math.floor(remaining / 60_000);
  if (minutes < 1) return "caduca en menos de un minuto";
  if (minutes < 60) return `caduca en ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `caduca en ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "caduca en 1 día" : `caduca en ${days} días`;
}

/**
 * The previews still on the books, newest pull request first.
 *
 * Includes `conflict` and `failed`: both occupy their pull request's slot until
 * somebody destroys them, and a list that hid them would leave a person
 * wondering why asking again does nothing. Excludes only what is destroyed —
 * the row survives as history, and history is not what a card is for.
 */
export function livePreviewsOf(rows: ReadonlyArray<PrPreviewRow>): ReadonlyArray<PrPreviewRow> {
  return rows
    .filter((row) => row.destroyedAt === null && row.status !== "destroyed")
    .toSorted((left, right) => right.prNumber - left.prNumber);
}

/**
 * The previews that actually hold disk, which is what the project's ceiling
 * counts. A `conflict` has no worktree and no database, so it is live without
 * being alive — the same distinction the server makes.
 */
export function alivePreviewsOf(rows: ReadonlyArray<PrPreviewRow>): ReadonlyArray<PrPreviewRow> {
  return livePreviewsOf(rows).filter(
    (row) => row.status === "ready" || row.status === "provisioning",
  );
}

/**
 * The live preview of THIS pull request, if any.
 *
 * What turns «Preview aislada» into «Ver preview» + «Destruir preview» in the
 * pull request panel: asking for a second one is idempotent on the server, so
 * the button would return the same preview and teach nobody anything.
 */
export function previewOfPullRequest(
  rows: ReadonlyArray<PrPreviewRow>,
  prNumber: number,
): PrPreviewRow | null {
  return livePreviewsOf(rows).find((row) => row.prNumber === prNumber) ?? null;
}
